import React, { useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Typography } from '@mui/material'
import { CalendarIcon, ListBulletIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import {
  useGetUpcomingActivitiesListForGamer,
  useGetUpcomingActivityForGamerPolling
} from '../../../apiLayer/queryHooks/queries'
import { INTERAX_COLORS } from '../../../constants/colors'
import { PageHeader } from '../../../shared/components/PageHeader'
import { ActivityWithSession, ActivityWithSessions } from '../../../types/GeneralTypes'
import { PageWrapper } from '../../../shared/components'
import { GamerRoutes } from '../../../config/routes/routeEnums'
import { isAIDebriefActivity } from '../../../../../pure-js/libs/isAIDebriefActivity'
import { useUser } from '../../../hooks/useUser'
import { useGlobalWhereby } from '../../../shared/hooks/useGlobalWhereby'
import { NextActivityGamerComponent, WhereByGroupCallComponent } from '../components'
import { GameDesktopOnlyModal } from '../components/GameDesktopOnlyModal'
import { useMobileGameGuard } from '../hooks/useMobileGameGuard'
import { pickNextActivityForCard } from '../helpers/activityHelpers'
import { Button, Spinner } from '../../../interaxDesignSystemComponents'

/**
 * UpcomingActivitiesPage - Activity Center view for the gamer
 * Shows the next activity widget + group call, plus a link to the full activity list
 */
export const UpcomingActivitiesPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { startCall } = useGlobalWhereby()
  const { data: userData } = useUser()

  const { data: upcomingActivitiesResponse, isLoading: loading } = useGetUpcomingActivitiesListForGamer()
  const { data: nextActivityResponse } = useGetUpcomingActivityForGamerPolling()

  const upcomingActivities = useMemo<ActivityWithSessions[]>(() => {
    return upcomingActivitiesResponse?.data ?? []
  }, [upcomingActivitiesResponse])

  const nextActivity = useMemo(
    () =>
      pickNextActivityForCard(
        nextActivityResponse?.data as ActivityWithSession | undefined,
        upcomingActivities,
        userData?.teamIds || []
      ),
    [nextActivityResponse, upcomingActivities, userData?.teamIds]
  )

  const { guardGameEntry, isDesktopOnlyModalOpen, closeDesktopOnlyModal } = useMobileGameGuard()

  // Game activities are desktop-only: on phones/tablets the guard swallows the enter-lobby
  // action (no Whereby call, no navigation) and shows the desktop-only notice instead.
  const handleEnterLobby = useCallback(() => {
    guardGameEntry(() => {
      startCall()
      navigate('/gamer/play-game-without-introduction')
    })
  }, [guardGameEntry, startCall, navigate])

  const isAIDebriefAfterNext = useMemo(() => {
    if (!nextActivity?.activity || upcomingActivities.length < 2) return false
    const nextId = nextActivity.activity.id
    const idx = upcomingActivities.findIndex((a) => a.id === nextId)
    const afterActivity = idx !== -1 ? upcomingActivities[idx + 1] : upcomingActivities[1]
    return afterActivity != null && isAIDebriefActivity(afterActivity.type)
  }, [nextActivity, upcomingActivities])

  const handleShowAll = useCallback(() => {
    navigate(GamerRoutes.ALL_UPCOMING_ACTIVITIES)
  }, [navigate])

  if (loading) {
    return (
      <PageWrapper sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spinner />
      </PageWrapper>
    )
  }

  return (
    <PageWrapper className="upcoming-activities-page">
      {/* Header */}
      <PageHeader
        badgeText={t('gamer.upcomingActivities.badge')}
        badgeIcon={<CalendarIcon />}
        title={t('gamer.upcomingActivities.title')}
        subtitle={t('gamer.upcomingActivities.subtitle')}
      />

      {/* Hero section — Activity Center: Next Activity + Group Call */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr', lg: '1fr 1fr' },
          gap: 2,
          mb: 3
        }}
      >
        <NextActivityGamerComponent
          activity={nextActivity?.activity}
          isFromFallback={nextActivity?.isFromFallback || false}
          userTeamIds={userData?.teamIds || []}
          onEnterLobby={handleEnterLobby}
        />
        <WhereByGroupCallComponent />
      </Box>

      {/* Hint: AI debrief follows next game session */}
      {isAIDebriefAfterNext && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: INTERAX_COLORS.thunderGrey }}>
            {t('gamer.upcomingActivities.nextAfterHint')}
          </Typography>
        </Box>
      )}

      {/* Show all activities link */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Button variant="secondary" onClick={handleShowAll} icon={<ListBulletIcon />}>
          {t('gamer.upcomingActivities.showAllActivities')}
        </Button>
      </Box>

      <GameDesktopOnlyModal isOpen={isDesktopOnlyModalOpen} onClose={closeDesktopOnlyModal} />
    </PageWrapper>
  )
}
